<script lang="ts">
	import type { SubNavAnchors } from './types';
	import { event } from '$lib/utils/tracking';
	import SubNavAnchor from '$lib/buttons/subNavAnchor.svelte';
	import { afterNavigate, replaceState } from '$app/navigation';
	import BtnPrimary from '$lib/buttons/v2/btnPrimary.svelte';
	import { onMount } from 'svelte';
	import { slide } from 'svelte/transition';
	import { page } from '$app/state';

	interface Props {
		entries: SubNavAnchors['entries'];
		title: string;
		ctaLabel: string;
		ctaTarget: string;
	}

	let { entries, title, ctaLabel, ctaTarget }: Props = $props();

	afterNavigate(({ from, to }) => {
		if (from === null || from?.route.id === to?.route.id) return;

		window.scrollTo(0, 0);
	});

	onMount(() => {
		const scrollObserver = new IntersectionObserver(
			(entries) => {
				for (const { target, isIntersecting } of entries) {
					let activeAnchor = page.state.activeAnchor;

					if (isIntersecting) {
						activeAnchor = target.id;
					} else if (activeAnchor === target.id) {
						activeAnchor = null;
					}

					if (activeAnchor !== page.state.activeAnchor) {
						replaceState('', { ...page.state, activeAnchor });
					}
				}
			},
			{ rootMargin: '-45% 0px' }
		);

		for (const entry of entries) {
			const element = document.getElementById(entry.anchor);

			if (!element) continue;

			scrollObserver.observe(element);
		}

		return () => scrollObserver.disconnect();
	});
</script>

<div
	id="subnav"
	class="relative z-10 h-12 bg-stone-5 lg:h-full"
	transition:slide={{ duration: 300 }}
>
	<nav
		class="scrollbar-hide flex h-full items-center justify-between overflow-x-auto px-6 md:px-8 xl:px-12"
	>
		<div class="flex items-center gap-x-5 lg:gap-x-8">
			<a
				href="#top"
				class="whitespace-nowrap py-2 text-[16px]/[140%] font-semibold text-brown-2 lg:py-3 lg:text-[18px]/[100%]"
				>{title}</a
			>
			{#each entries as entry (entry.anchor)}
				<SubNavAnchor active={page.state.activeAnchor === entry.anchor} anchor={entry.anchor}>
					{entry.name}
				</SubNavAnchor>
			{/each}
		</div>

		<BtnPrimary
			class="my-3 whitespace-nowrap pl-6 max-lg:hidden"
			href={ctaTarget}
			size="regular"
			onclick={() => event('Buy now button clicked')}
			data-testid="btn-sub-nav-reserve">{ctaLabel}</BtnPrimary
		>
	</nav>
</div>

<style>
	div#subnav {
		box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.04);
	}
</style>
